import { computedEager } from "@vueuse/core";
import { createRouter as _createRouter, createWebHistory } from "vue-router";
import { useThemeContext } from "@/core/composables";
import { VirtoPermissions } from "@/core/enums";
import { buildRedirectUrl, getReturnUrlValue } from "@/core/utilities";
import { ROUTES } from "@/router/routes/constants";
import { useUser } from "@/shared/account";
import { mainRoutes } from "./routes";
import type { RouteRecordName } from "vue-router";

export function createRouter(options: { base: string }) {
  const { base } = options;
  const { isAuthenticated, isAdministrator, operator, organization, checkPermissions } = useUser();
  const { themeContext } = useThemeContext();
  const loginOnBehalf = computedEager<boolean>(() => checkPermissions(VirtoPermissions.LoginOnBehalf));

  const router = _createRouter({
    routes: mainRoutes,
    history: createWebHistory(base),
    scrollBehavior(to, from, savedPosition) {
      if (to.path !== from.path) {
        return savedPosition || { top: 0 };
      } else {
        return undefined;
      }
    },
  });

  router.beforeEach((to, _, next) => {
    // Protecting routes
    // Innovadis custom: Allow access to all routes in preview mode
    const unauthorizedAccessIsDenied: boolean =
      !isAuthenticated.value &&
      !to.meta.public &&
      (to.meta.requiresAuth || !themeContext.value.storeSettings.anonymousUsersAllowed);

    if (unauthorizedAccessIsDenied) {
      // save current location to return to it after sign in
      const query = buildRedirectUrl(to) || {};

      return next({
        name: ROUTES.SIGN_IN.NAME,
        query,
      });
    }

    // Innovadis custom: route to profile
    // Protecting company routes
    if (to.meta.requiresOrganization && !organization.value) {
      return next({ name: "Profile" });
    }

    // Make Dashboard the default Home page for authorized users
    // Make Dashboard the default Home page for authorized users
    if (
      (isAdministrator.value && !operator.value && Array<RouteRecordName>("SignIn").includes(to.name!)) ||
      (loginOnBehalf.value && !operator.value && Array<RouteRecordName>("SignIn").includes(to.name!))
    ) {
      return next({ name: "SignInAdministrator" });
    } else if (
      isAuthenticated.value &&
      Array<RouteRecordName>(
        "Home",
        ROUTES.SIGN_IN.NAME,
        "SignUp",
        "ForgotPassword",
        "ResetPassword",
        "SetPassword",
        "ConfirmInvitation",
      ).includes(to.name)
    ) {
      // Innovadis custom: route to profile
      return next(getReturnUrlValue() || { name: "Profile" });
    }

    return next();
  });

  return router;
}
